import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportPreviewModal = ({ exportItem, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('preview');

  if (!isOpen || !exportItem) return null;

  const mockPreviewData = [
    {
      transactionType: 'SI',
      accountRef: 'ACME001',
      nominalCode: '4000',
      date: '14/10/2025',
      reference: 'INV-2025-001',
      details: 'Office Supplies - Stationery',
      netAmount: '125.00',
      taxCode: 'T1',
      taxAmount: '25.00',
      exchangeRate: '1.0000',
      grossAmount: '150.00'
    },
    {
      transactionType: 'SI',
      accountRef: 'ACME001',
      nominalCode: '4001',
      date: '14/10/2025',
      reference: 'INV-2025-001',
      details: 'Office Supplies - Equipment',
      netAmount: '200.00',
      taxCode: 'T1',
      taxAmount: '40.00',
      exchangeRate: '1.0000',
      grossAmount: '240.00'
    },
    {
      transactionType: 'SI',
      accountRef: 'TECHCO002',
      nominalCode: '4000',
      date: '14/10/2025',
      reference: 'INV-2025-002',
      details: 'Software License',
      netAmount: '500.00',
      taxCode: 'T1',
      taxAmount: '100.00',
      exchangeRate: '1.0000',
      grossAmount: '600.00'
    }
  ];

  const mockValidationResults = {
    status: 'valid',
    errors: [],
    warnings: [
      'Exchange rate defaulted to 1.0000 for GBP transactions',
      'Tax code T1 mapped to standard 20% VAT rate'
    ],
    fieldMapping: {
      'Transaction Type': 'SI (Sales Invoice)',
      'Account Reference': 'Vendor account code from matching rules',
      'Nominal Code': 'Product/service category code',
      'Date': 'Invoice date in DD/MM/YYYY format',
      'Reference': 'Invoice number from OCR extraction',
      'Details': 'Line item description',
      'Net Amount': 'Amount excluding VAT',
      'Tax Code': 'VAT rate identifier (T1=20%, T0=0%, T2=5%)',
      'Tax Amount': 'VAT amount calculated',
      'Exchange Rate': 'Currency conversion rate',
      'Gross Amount': 'Total amount including VAT'
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon 
              name={exportItem?.format === 'CSV' ? 'FileText' : 'FileSpreadsheet'} 
              size={24} 
              className="text-primary" 
            />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Export Preview</h2>
              <p className="text-sm text-muted-foreground">{exportItem?.filename}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="h-8 w-8 p-0"
          />
        </div>

        {/* Export Info */}
        <div className="px-6 py-4 bg-muted/30 border-b border-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Export Date:</span>
              <div className="font-medium text-foreground">{formatDate(exportItem?.exportDate)}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Type:</span>
              <div className="font-medium text-foreground">{exportItem?.type}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Invoices:</span>
              <div className="font-medium text-foreground">{exportItem?.invoiceCount}</div>
            </div>
            <div>
              <span className="text-muted-foreground">File Size:</span>
              <div className="font-medium text-foreground">{formatFileSize(exportItem?.fileSize)}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('preview')}
            className={`
              px-6 py-3 text-sm font-medium border-b-2 transition-smooth
              ${activeTab === 'preview' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }
            `}
          >
            Data Preview
          </button>
          <button
            onClick={() => setActiveTab('validation')}
            className={`
              px-6 py-3 text-sm font-medium border-b-2 transition-smooth
              ${activeTab === 'validation' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }
            `}
          >
            Validation
          </button>
          <button
            onClick={() => setActiveTab('mapping')}
            className={`
              px-6 py-3 text-sm font-medium border-b-2 transition-smooth
              ${activeTab === 'mapping' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }
            `}
          >
            Field Mapping
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {activeTab === 'preview' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-foreground">Type</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground">Account</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground">Nominal</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground">Date</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground">Reference</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground">Details</th>
                    <th className="text-right px-3 py-2 font-medium text-foreground">Net</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground">Tax</th>
                    <th className="text-right px-3 py-2 font-medium text-foreground">VAT</th>
                    <th className="text-right px-3 py-2 font-medium text-foreground">Gross</th>
                  </tr>
                </thead>
                <tbody>
                  {mockPreviewData?.map((row, index) => (
                    <tr key={index} className="border-b border-border">
                      <td className="px-3 py-2 font-mono text-foreground">{row?.transactionType}</td>
                      <td className="px-3 py-2 font-mono text-foreground">{row?.accountRef}</td>
                      <td className="px-3 py-2 font-mono text-foreground">{row?.nominalCode}</td>
                      <td className="px-3 py-2 text-foreground">{row?.date}</td>
                      <td className="px-3 py-2 text-foreground">{row?.reference}</td>
                      <td className="px-3 py-2 text-foreground max-w-48 truncate">{row?.details}</td>
                      <td className="px-3 py-2 text-right font-mono text-foreground">£{row?.netAmount}</td>
                      <td className="px-3 py-2 font-mono text-foreground">{row?.taxCode}</td>
                      <td className="px-3 py-2 text-right font-mono text-foreground">£{row?.taxAmount}</td>
                      <td className="px-3 py-2 text-right font-mono text-foreground">£{row?.grossAmount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'validation' && (
            <div className="space-y-6">
              {/* Validation Status */}
              <div className="flex items-center space-x-3">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  ${mockValidationResults?.status === 'valid' ? 'bg-success/10' : 'bg-error/10'}
                `}>
                  <Icon 
                    name={mockValidationResults?.status === 'valid' ? 'CheckCircle' : 'XCircle'} 
                    size={20}
                    className={mockValidationResults?.status === 'valid' ? 'text-success' : 'text-error'}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {mockValidationResults?.status === 'valid' ? 'Validation Passed' : 'Validation Failed'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Export file meets Sage 50 format requirements
                  </p>
                </div>
              </div>

              {/* Warnings */}
              {mockValidationResults?.warnings?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-warning" />
                    <span>Warnings</span>
                  </h4>
                  <div className="space-y-2">
                    {mockValidationResults?.warnings?.map((warning, index) => (
                      <div key={index} className="flex items-start space-x-2 p-3 bg-warning/5 border border-warning/20 rounded-md">
                        <Icon name="Info" size={16} className="text-warning mt-0.5" />
                        <span className="text-sm text-foreground">{warning}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">{mockPreviewData?.length}</div>
                  <div className="text-sm text-muted-foreground">Valid Records</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">£990.00</div>
                  <div className="text-sm text-muted-foreground">Total Amount</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mapping' && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground mb-4">
                Field mapping configuration for Sage 50 export format
              </div>
              {Object.entries(mockValidationResults?.fieldMapping)?.map(([field, description]) => (
                <div key={field} className="flex items-start space-x-3 p-3 border border-border rounded-md">
                  <div className="w-32 flex-shrink-0">
                    <span className="text-sm font-medium text-foreground">{field}</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-muted-foreground">{description}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {exportItem?.status === 'completed' && (
            <Button 
              variant="default" 
              iconName="Download" 
              iconPosition="left"
              onClick={() => {
                // Handle download
                onClose();
              }}
            >
              Download File
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportPreviewModal;
